import { safeLocalStorageSetJson } from "../lib/storageQuotaRecovery";
import React from "react";
import { shouldShowAdmin, shouldShowBlockingBoot, shouldShowGestor, shouldShowLogin } from "./AppGuards";

const ADMIN_PANEL_RETRY_KEY = "fz:adminPanelChunkRetryCount";
const ADMIN_PANEL_MAX_RETRIES = 3;

function BootLoadingScreen({
  VersionUpdateOverlay,
  vcheck,
  preLoginStageLabels,
  preLoginStage,
  preSyncing,
  preLoginCountdown,
  preLoginProgress,
  CLIENT_VERSION,
  navigatorObj,
}) {
  return (
    <>
      <VersionUpdateOverlay vcheck={vcheck}/>
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:24,background:"linear-gradient(180deg,#0F172A,#111827)"}}>
        <div style={{width:"100%",maxWidth:420,background:"rgba(15,23,42,.9)",border:"1px solid rgba(148,163,184,.18)",borderRadius:20,padding:24,boxShadow:"0 20px 50px rgba(0,0,0,.35)",color:"#fff"}}>
          <div style={{fontSize:36,marginBottom:12,textAlign:"center"}}>{vcheck.state==="checking"?"🛰️":"🔄"}</div>
          <div style={{fontSize:22,fontWeight:800,textAlign:"center",marginBottom:8}}>{vcheck.state==="checking"?"Verificando versão do app":"Preparando login"}</div>
          <div style={{fontSize:14,color:"#94A3B8",textAlign:"center",lineHeight:1.5,marginBottom:18}}>
            {vcheck.state==="checking"
              ? "Conferindo se este dispositivo está rodando a versão mais recente antes de liberar o acesso."
              : "Sincronizando dados do dispositivo e das fazendas antes do login."}
          </div>
          <div style={{display:"flex",justifyContent:"center",marginBottom:14}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:8,padding:"7px 14px",borderRadius:999,background:"rgba(59,130,246,.10)",border:"1px solid rgba(96,165,250,.20)",boxShadow:"0 8px 24px rgba(2,6,23,.12)"}}>
              <span style={{width:8,height:8,borderRadius:999,background:"#60A5FA",boxShadow:"0 0 0 0 rgba(96,165,250,.55)",animation:"fzPulseDot 1.8s ease-in-out infinite"}} />
              <span style={{fontSize:12,fontWeight:800,color:"#DBEAFE",letterSpacing:".04em",textTransform:"uppercase"}}>{vcheck.state==="checking" ? "Checando versão" : (preLoginStageLabels[preLoginStage] || "Preparando acesso")}</span>
            </div>
          </div>
          <div style={{display:"grid",gap:10,marginBottom:8}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}><span style={{color:"#94A3B8"}}>Versão local</span><strong>{CLIENT_VERSION}</strong></div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}><span style={{color:"#94A3B8"}}>Conexão</span><strong>{navigatorObj?.onLine?"Online":"Offline"}</strong></div>
            {preSyncing && <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}><span style={{color:"#94A3B8"}}>Sincronização</span><strong>Em andamento</strong></div>}
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:74,margin:"6px 0 10px"}}>
            <div style={{display:"inline-flex",alignItems:"baseline",gap:6,padding:"8px 14px",borderRadius:999,background:"rgba(15,23,42,.45)",border:"1px solid rgba(148,163,184,.18)",boxShadow:"0 8px 24px rgba(2,6,23,.18)"}}>
              <span style={{fontSize:34,fontWeight:900,lineHeight:1,color:"#F8FAFC",fontVariantNumeric:"tabular-nums",letterSpacing:".02em"}}>{preLoginCountdown}</span>
              <span style={{fontSize:12,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:".14em"}}>s</span>
            </div>
          </div>
          <div style={{width:"100%",height:10,background:"rgba(30,41,59,.95)",borderRadius:999,overflow:"hidden",position:"relative",boxShadow:"inset 0 1px 2px rgba(255,255,255,.04), inset 0 -1px 4px rgba(0,0,0,.28)"}}>
            <div style={{height:"100%",width:`${preLoginProgress}%`,background:"linear-gradient(90deg,#3B82F6 0%,#2563EB 28%,#0EA5E9 55%,#8B5CF6 82%,#3B82F6 100%)",backgroundSize:"220% 100%",borderRadius:999,animation:"fzBarShift 1.8s linear infinite",boxShadow:"0 0 18px rgba(59,130,246,.28)",transition:"width .55s ease"}} />
            <div style={{position:"absolute",inset:0,overflow:"hidden",borderRadius:999,pointerEvents:"none"}}>
              <div style={{position:"absolute",top:0,bottom:0,width:"36%",background:"linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.30),rgba(255,255,255,0))",animation:"fzShimmer 1.9s ease-in-out infinite"}} />
            </div>
          </div>
          <div style={{fontSize:12,color:"#64748B",textAlign:"center",marginTop:12}}>
            A atualização de fazendas e contas pode continuar em segundo plano, sem travar a tela de login.
          </div>
        </div>
      </div>
    </>
  );
}

function isChunkLoadError(error) {
  const message = String(error?.message || error || "").toLowerCase();
  const name = String(error?.name || "").toLowerCase();
  return (
    name.includes("chunkloaderror") ||
    message.includes("loading chunk") ||
    message.includes("chunkloaderror") ||
    message.includes("failed to fetch dynamically imported module")
  );
}

function readAdminRetryCount() {
  try {
    return Math.max(0, Number(sessionStorage.getItem(ADMIN_PANEL_RETRY_KEY) || 0));
  } catch (_) {
    return 0;
  }
}

function writeAdminRetryCount(value) {
  try {
    sessionStorage.setItem(ADMIN_PANEL_RETRY_KEY, String(Math.max(0, Number(value) || 0)));
  } catch (_) {}
}

function clearAdminRetryCount() {
  try {
    sessionStorage.removeItem(ADMIN_PANEL_RETRY_KEY);
  } catch (_) {}
}

function triggerAdminPanelReload() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("adminReload", String(Date.now()));
    window.location.replace(url.toString());
    return;
  } catch (_) {}
  try {
    window.location.reload();
  } catch (_) {}
}

function AdminPanelLoadingScreen({ attempt = 0, mode = "loading" }) {
  const steps = React.useMemo(() => {
    if (mode === "retrying") {
      return [
        "Recarregando recursos do Painel Admin",
        "Verificando arquivos atualizados",
        "Tentando abrir a área administrativa novamente",
      ];
    }
    return [
      "Carregando interface administrativa",
      "Preparando recursos do painel",
      "Sincronizando componentes do Admin",
      "Finalizando abertura do Painel Admin",
    ];
  }, [mode]);

  const [progress, setProgress] = React.useState(mode === "retrying" ? 32 : 12);
  const [stepIndex, setStepIndex] = React.useState(0);

  React.useEffect(() => {
    const progressTimer = window.setInterval(() => {
      setProgress((prev) => {
        const cap = mode === "retrying" ? 92 : 96;
        const next = prev + (prev < 45 ? 8 : prev < 72 ? 5 : prev < 86 ? 3 : 1.2);
        return Math.min(cap, next);
      });
    }, 350);
    const stepTimer = window.setInterval(() => {
      setStepIndex((prev) => Math.min(steps.length - 1, prev + 1));
    }, 1100);
    return () => {
      window.clearInterval(progressTimer);
      window.clearInterval(stepTimer);
    };
  }, [mode, steps.length]);

  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:24,background:"linear-gradient(180deg,#0F172A,#111827)"}}>
      <div style={{width:"100%",maxWidth:520,background:"rgba(15,23,42,.92)",border:"1px solid rgba(148,163,184,.18)",borderRadius:24,padding:26,boxShadow:"0 24px 60px rgba(0,0,0,.35)",color:"#fff"}}>
        <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
          <div style={{width:72,height:72,borderRadius:20,display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,rgba(59,130,246,.22),rgba(139,92,246,.18))",border:"1px solid rgba(96,165,250,.22)",fontSize:34}}>
            {mode === "retrying" ? "🛠️" : "🧭"}
          </div>
        </div>
        <div style={{fontSize:24,fontWeight:900,textAlign:"center",marginBottom:8}}>
          {mode === "retrying" ? "Recuperando Painel Admin" : "Preparando Painel Admin"}
        </div>
        <div style={{fontSize:14,color:"#94A3B8",textAlign:"center",lineHeight:1.55,marginBottom:18}}>
          {mode === "retrying"
            ? "Houve uma falha temporária ao carregar um arquivo do Painel Admin. O app está tentando recuperar automaticamente."
            : "Carregando a interface administrativa, sincronizando recursos essenciais e preparando a abertura do painel."}
        </div>
        <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:8,padding:"7px 14px",borderRadius:999,background:"rgba(59,130,246,.10)",border:"1px solid rgba(96,165,250,.20)"}}>
            <span style={{width:8,height:8,borderRadius:999,background:"#60A5FA",boxShadow:"0 0 0 0 rgba(96,165,250,.55)",animation:"fzPulseDot 1.8s ease-in-out infinite"}} />
            <span style={{fontSize:12,fontWeight:800,color:"#DBEAFE",letterSpacing:".05em",textTransform:"uppercase"}}>
              {mode === "retrying" ? `Tentativa automática ${Math.min(ADMIN_PANEL_MAX_RETRIES, Math.max(1, attempt))}/${ADMIN_PANEL_MAX_RETRIES}` : "Abrindo área administrativa"}
            </span>
          </div>
        </div>
        <div style={{width:"100%",height:12,background:"rgba(30,41,59,.95)",borderRadius:999,overflow:"hidden",position:"relative",boxShadow:"inset 0 1px 2px rgba(255,255,255,.04), inset 0 -1px 4px rgba(0,0,0,.28)"}}>
          <div style={{height:"100%",width:`${progress}%`,background:"linear-gradient(90deg,#3B82F6 0%,#2563EB 28%,#0EA5E9 55%,#8B5CF6 82%,#3B82F6 100%)",backgroundSize:"220% 100%",borderRadius:999,animation:"fzBarShift 1.8s linear infinite",boxShadow:"0 0 18px rgba(59,130,246,.28)",transition:"width .45s ease"}} />
          <div style={{position:"absolute",inset:0,overflow:"hidden",borderRadius:999,pointerEvents:"none"}}>
            <div style={{position:"absolute",top:0,bottom:0,width:"36%",background:"linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.30),rgba(255,255,255,0))",animation:"fzShimmer 1.9s ease-in-out infinite"}} />
          </div>
        </div>
        <div style={{display:"grid",gap:10,marginTop:18}}>
          {steps.map((step, idx) => {
            const done = idx < stepIndex;
            const active = idx === stepIndex;
            return (
              <div key={`${mode}-${idx}`} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:14,background:active ? "rgba(59,130,246,.10)" : "rgba(15,23,42,.45)",border:`1px solid ${active ? "rgba(96,165,250,.22)" : "rgba(148,163,184,.12)"}`}}>
                <div style={{width:22,height:22,borderRadius:999,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,background:done ? "rgba(16,185,129,.18)" : active ? "rgba(59,130,246,.20)" : "rgba(148,163,184,.10)",color:done ? "#34D399" : active ? "#93C5FD" : "#94A3B8"}}>
                  {done ? "✓" : active ? "•" : idx + 1}
                </div>
                <div style={{fontSize:13,color:active ? "#E2E8F0" : "#94A3B8"}}>{step}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AdminPanelErrorScreen({ attempt = 0, onReload, onBack, errorMessage = "" }) {
  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:24,background:"linear-gradient(180deg,#0F172A,#111827)"}}>
      <div style={{width:"100%",maxWidth:540,background:"rgba(15,23,42,.92)",border:"1px solid rgba(248,113,113,.22)",borderRadius:24,padding:26,boxShadow:"0 24px 60px rgba(0,0,0,.35)",color:"#fff"}}>
        <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
          <div style={{width:72,height:72,borderRadius:20,display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,rgba(248,113,113,.18),rgba(251,191,36,.12))",border:"1px solid rgba(248,113,113,.22)",fontSize:34}}>
            ⚠️
          </div>
        </div>
        <div style={{fontSize:24,fontWeight:900,textAlign:"center",marginBottom:8}}>Painel Admin ainda não carregou</div>
        <div style={{fontSize:14,color:"#94A3B8",textAlign:"center",lineHeight:1.55,marginBottom:18}}>
          O app já tentou recuperar automaticamente o carregamento do Painel Admin {attempt > 0 ? `(${attempt}/${ADMIN_PANEL_MAX_RETRIES})` : ""}. Você pode tentar recarregar o painel agora.
        </div>
        {!!errorMessage && (
          <div style={{fontSize:12,color:"#FCA5A5",background:"rgba(127,29,29,.22)",border:"1px solid rgba(248,113,113,.16)",borderRadius:14,padding:"10px 12px",marginBottom:18,wordBreak:"break-word"}}>
            {errorMessage}
          </div>
        )}
        <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
          <button onClick={onReload} style={{border:0,borderRadius:14,padding:"12px 16px",fontWeight:800,background:"#2563EB",color:"#fff",cursor:"pointer"}}>
            Recarregar Painel Admin
          </button>
          <button onClick={onBack} style={{border:"1px solid rgba(148,163,184,.18)",borderRadius:14,padding:"12px 16px",fontWeight:800,background:"rgba(15,23,42,.45)",color:"#E2E8F0",cursor:"pointer"}}>
            Voltar
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminPanelEntry({
  session,
  farms,
  handleLogout,
  themeRevision,
  persistSession,
  adminDeps,
}) {
  const [retryCount, setRetryCount] = React.useState(() => readAdminRetryCount());
  const [chunkError, setChunkError] = React.useState(null);
  const [mode, setMode] = React.useState("loading");
  const [adminModule, setAdminModule] = React.useState(null);
  const [ready, setReady] = React.useState(false);

  const loadAdminModule = React.useCallback(async () => {
    setMode((prev) => (retryCount > 0 ? "retrying" : prev || "loading"));
    setChunkError(null);
    try {
      const mod = await import("../admin/AdminModule");
      const Comp = mod?.default || null;
      if (!Comp) throw new Error("Painel Admin indisponível.");
      setAdminModule(() => Comp);
      setReady(false);
    } catch (error) {
      setAdminModule(null);
      setChunkError(error);
    }
  }, [retryCount]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadAdminModule();
      } catch (_) {}
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAdminModule]);

  React.useEffect(() => {
    if (!chunkError || !isChunkLoadError(chunkError)) return;
    if (retryCount >= ADMIN_PANEL_MAX_RETRIES) return;
    const nextAttempt = retryCount + 1;
    writeAdminRetryCount(nextAttempt);
    setRetryCount(nextAttempt);
    setMode("retrying");
    const timer = window.setTimeout(() => {
      loadAdminModule();
    }, nextAttempt === 1 ? 900 : nextAttempt === 2 ? 1600 : 2400);
    return () => window.clearTimeout(timer);
  }, [chunkError, retryCount, loadAdminModule]);

  React.useEffect(() => {
    if (!ready) return;
    clearAdminRetryCount();
    setRetryCount(0);
    setChunkError(null);
    setMode("ready");
  }, [ready]);

  if (chunkError && retryCount >= ADMIN_PANEL_MAX_RETRIES) {
    return (
      <AdminPanelErrorScreen
        attempt={retryCount}
        errorMessage={String(chunkError?.message || chunkError || "")}
        onReload={() => {
          writeAdminRetryCount(0);
          setRetryCount(0);
          setMode("loading");
          setChunkError(null);
          setAdminModule(null);
          triggerAdminPanelReload();
        }}
        onBack={handleLogout}
      />
    );
  }

  if (!adminModule) {
    return <AdminPanelLoadingScreen attempt={retryCount} mode={mode === "retrying" ? "retrying" : "loading"} />;
  }

  const LoadedAdminPanel = adminModule;
  return (
    <LoadedAdminPanel
      session={session}
      farms={farms}
      onLogout={handleLogout}
      themeRevision={themeRevision}
      onSessionUpdate={persistSession}
      adminDeps={adminDeps}
      onAdminReady={() => {
        setReady(true);
      }}
    />
  );
}

export default function AppRoutes(props) {
  const {
    session,
    maintInfo,
    exitMaintenance,
    vcheck,
    preLoginStageLabels,
    preLoginStage,
    preSyncing,
    preLoginCountdown,
    preLoginProgress,
    CLIENT_VERSION,
    navigatorObj,
    farms,
    handleLogin,
    handleLogout,
    themeRevision,
    setSession,
    activeFarm,
    FarmCtx,
    MaintenanceModal,
    VersionUpdateOverlay,
    LoginScreen,
    loginScreenDeps,
    GestorPanel,
    gestorPanelDeps,
    FarmDashboard,
    farmDashboardDeps,
    adminDeps,
  } = props;

  if (maintInfo && session?.role !== "admin") {
    return <MaintenanceModal info={maintInfo} isAdmin={false} onAdminExit={() => {}}/>;
  }

  if (maintInfo && session?.role === "admin") {
    return <MaintenanceModal info={maintInfo} isAdmin={true} onAdminExit={exitMaintenance}/>;
  }

  if (shouldShowLogin({ session })) {
    const blockingVersionCheck = false;
    const blockingPreSync = false;

    if (shouldShowBlockingBoot({ session, blockingVersionCheck, blockingPreSync })) {
      return (
        <BootLoadingScreen
          VersionUpdateOverlay={VersionUpdateOverlay}
          vcheck={vcheck}
          preLoginStageLabels={preLoginStageLabels}
          preLoginStage={preLoginStage}
          preSyncing={preSyncing}
          preLoginCountdown={preLoginCountdown}
          preLoginProgress={preLoginProgress}
          CLIENT_VERSION={CLIENT_VERSION}
          navigatorObj={navigatorObj}
        />
      );
    }

    return (
      <>
        <VersionUpdateOverlay vcheck={vcheck}/>
        <LoginScreen farms={farms} onLogin={handleLogin} deps={loginScreenDeps}/>
      </>
    );
  }

  const persistSession = (nextSession) => {
    try {
      setSession(nextSession);
      safeLocalStorageSetJson("fz:session", nextSession);
    } catch (_) {}
  };

  if (shouldShowAdmin({ session })) {
    return (
      <AdminPanelEntry
        session={session}
        farms={farms}
        handleLogout={handleLogout}
        themeRevision={themeRevision}
        persistSession={persistSession}
        adminDeps={adminDeps}
      />
    );
  }

  if (shouldShowGestor({ session })) {
    return (
      <GestorPanel
        session={session}
        farms={farms}
        onLogout={handleLogout}
        onSessionUpdate={persistSession}
        deps={gestorPanelDeps}
      />
    );
  }

  return (
    <FarmCtx.Provider value={{fid:activeFarm?.id,farm:activeFarm}}>
      <FarmDashboard
        farm={activeFarm}
        session={session}
        isGestor={false}
        themeRevision={themeRevision}
        onBack={handleLogout}
        onLogout={handleLogout}
        onSessionUpdate={persistSession}
        deps={farmDashboardDeps}
      />
    </FarmCtx.Provider>
  );
}
